import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import SnapshotPE from '../components/snapshots/SnapshotPE';
import SnapshotPP from '../components/snapshots/SnapshotPP';
import SnapshotPS from '../components/snapshots/SnapshotPS';
import SnapshotPVC from '../components/snapshots/SnapshotPVC';
import SnapshotExportButton from '../components/snapshots/SnapshotExportButton';
import AIPanel from '../components/AIPanel';
import FreeTrialFade from '@/components/common/FreeTrialFade';
import { apiClient } from '@rti/shared';
import { ArrowLeft } from 'lucide-react';

const MonthlySnapshot: React.FC = () => {
  const { pathId } = useParams<{ pathId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [commentaryType, setCommentaryType] = useState<'preliminary' | 'final'>('preliminary');
  const [selectedDate, setSelectedDate] = useState<string>('');

  const isPaidSubscriber = (user?.subscription_status || '').toLowerCase() === 'active';
  const isFreeTrial = !!user?.on_free_trial && !isPaidSubscriber;
  const isPageBlocked = isFreeTrial;

  useEffect(() => {
    if (!user) {
      navigate('/authorization');
    }
  }, [user]);

  const handleBackClick = () => {
    navigate(`/dashboard`);
  };

  const getSnapshotTitle = () => {
    switch (pathId) {
      case 'pe':
        return 'PE';
      case 'pp':
        return 'PP';
      case 'ps':
        return 'PS';
      case 'pvc':
        return 'PVC';
      default:
        return 'PE';
    }
  };

  const renderSnapshotComponent = () => {
    switch (pathId) {
      case 'pe':
        return <SnapshotPE onCommentaryTypeChange={setCommentaryType} onSelectedDateChange={setSelectedDate} />;
      case 'pp':
        return <SnapshotPP onCommentaryTypeChange={setCommentaryType} onSelectedDateChange={setSelectedDate} />;
      case 'ps':
        return <SnapshotPS onCommentaryTypeChange={setCommentaryType} onSelectedDateChange={setSelectedDate} />;
      case 'pvc':
        return <SnapshotPVC onCommentaryTypeChange={setCommentaryType} onSelectedDateChange={setSelectedDate} />;
      default:
        return (
          <div className="p-8">
            <h1 className="text-3xl font-bold mb-6">Monthly Snapshot Not Found</h1>
            <p>The requested monthly snapshot "{pathId}" is not available.</p>
            <button
              className="mt-4 py-2 px-4 bg-blue-600 text-white rounded hover:bg-blue-700"
              onClick={handleBackClick}
            >
              Return to Dashboard
            </button>
          </div>
        );
    }
  };

  const resinType = getSnapshotTitle() as 'PE' | 'PP' | 'PS' | 'PVC';

  return (
    <div className="min-h-screen flex flex-col p-5 w-full dashboard-shadow max-w-8xl mt-5">
      <div className="content-toolbar mb-5 flex items-center justify-between flex-shrink-0">
        <button
          className="py-5 px-8 jet "
          onClick={handleBackClick}
        >
          <ArrowLeft className="mr-2" />
          <span>Back to Landing Page</span>
        </button>
        {selectedDate && (
          <div title={isPageBlocked ? 'Subscribe to export all monthly snapshot data' : ''}>
            <SnapshotExportButton
              resinType={resinType}
              selectedDate={selectedDate}
              commentaryType={commentaryType}
              disabled={isPageBlocked}
            />
          </div>
        )}
      </div>

      <FreeTrialFade className="flex-1 flex flex-col" message="Subscribe to view monthly snapshot data" unlockTitle="Unlock this section" alignTop>
        <div className="flex flex-col gap-3 justify-between pt-5 pb-3">
          <p className="text-2xl font-light">
            Monthly {getSnapshotTitle()} Snapshots - {commentaryType === 'preliminary' ? 'Preliminary' : 'Final'}
              <span className='block mt-2 text-base font-light'>Choose the month of data you want to view.*</span>
          </p>

            <p data-source-tag className='mt-3 text-sm'><em>*ACC data is published one month after the actual period.</em></p>
        </div>
        <hr className="my-3 pb-5 -mx-5 border-rs-jet"/>

        {selectedDate && (
          <AIPanel
            fetchFn={(force) => apiClient.getSnapshotAISummary(resinType, commentaryType, force)}
            regenerateFn={(overrides) => apiClient.regenerateAiSummary(resinType, commentaryType, overrides)}
            context={`${resinType.toLowerCase()}-${commentaryType}`}
            resetKey={`${resinType}-${commentaryType}`}
            enabled={!!selectedDate}
          />
        )}

        {renderSnapshotComponent()}
      </FreeTrialFade>
    </div>
  );
};

export default MonthlySnapshot;
